import { useMsal } from '@azure/msal-react';
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { useCallback } from 'react';
import { apiScopes } from './msalConfig';

/**
 * Module-level guard so that a *burst* of token failures (a page typically
 * fires several API calls at once) triggers at most one interactive redirect.
 * Without this, concurrent `acquireTokenRedirect`/`logoutRedirect` calls collide
 * and MSAL can leave a stuck `interaction_in_progress` state.
 *
 * Once a redirect is kicked off the page navigates away, so this flag does not
 * need resetting on the happy path — only when the redirect call itself fails
 * before navigation.
 */
let redirectInProgress = false;

/**
 * Kick off an interactive (redirect) re-authentication exactly once.
 *
 * Swallows MSAL's `interaction_in_progress` error: if a redirect is already
 * underway, a second attempt is a no-op rather than a thrown error.
 */
async function triggerInteractiveAuth(
  msalInstance: IPublicClientApplication,
  account: AccountInfo
): Promise<void> {
  if (redirectInProgress) return;
  redirectInProgress = true;
  try {
    await msalInstance.acquireTokenRedirect({ ...apiScopes, account });
    // On success the browser navigates away; code below does not run.
  } catch (error) {
    if (
      error instanceof BrowserAuthError &&
      error.errorCode === 'interaction_in_progress'
    ) {
      // Another interaction is already mid-flight — nothing to do.
      return;
    }
    // Real failure before navigation: release the guard so a later attempt can retry.
    redirectInProgress = false;
    throw error;
  }
}

/**
 * Hook that provides a function to acquire an access token for the StaffLens API.
 *
 * Tries silent acquisition first (using cached/refresh token). If that fails
 * because user interaction is required (e.g. consent, MFA, expired refresh
 * token), falls back to a guarded redirect-based flow.
 *
 * Usage:
 *   const { getToken } = useAuthToken();
 *   const token = await getToken();
 *   // token is the Bearer access token string, or null if unauthenticated
 */
export function useAuthToken() {
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiScopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (isInteractionRequired(error)) {
        // Silent acquisition failed — redirect to Entra ID for interactive login.
        // After redirect, the page reloads and handleRedirectPromise in
        // AuthProvider processes the result.
        await triggerInteractiveAuth(instance, account);
        return null;
      }
      throw error;
    }
  }, [instance, accounts]);

  return { getToken };
}

/**
 * Standalone (non-hook) function to acquire a token using the singleton MSAL instance.
 * Useful for Axios interceptors or other non-React contexts.
 *
 * Resolve the singleton MSAL instance via the lazy getter:
 *   import { getMsalInstance } from './msalInstance';
 *   import { acquireTokenForApi } from './useAuthToken';
 *   const token = await acquireTokenForApi(getMsalInstance());
 */
export async function acquireTokenForApi(
  msalInstance: IPublicClientApplication
): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (isInteractionRequired(error)) {
      await triggerInteractiveAuth(msalInstance, account);
      return null;
    }
    throw error;
  }
}

/**
 * Force an interactive re-authentication for the current account.
 *
 * Called from the API client's 401 handler: the cached token was rejected by
 * the backend, so a silent acquisition would just hand back the same bad token.
 * This goes straight to an interactive redirect (guarded so concurrent 401s
 * trigger only one redirect). No-op if there is no account to re-auth.
 */
export async function forceReauth(
  msalInstance: IPublicClientApplication
): Promise<void> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return;
  await triggerInteractiveAuth(msalInstance, account);
}

/**
 * Whether a failed silent token acquisition should fall back to interactive
 * login. Covers the explicit interaction-required case plus stale/corrupted
 * browser cache states (e.g. tokens evicted out from under us) that silent
 * acquisition cannot recover from on its own.
 */
export function isInteractionRequired(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error instanceof BrowserAuthError) {
    return (
      error.errorCode === 'no_account_error' ||
      error.errorCode === 'no_tokens_found' ||
      error.errorCode === 'token_request_cache_error'
    );
  }
  return false;
}
